"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/feedback";
import { DEFAULT_MAKEUPS_ANNUAL_REQUEST_LIMIT } from "@/features/makeups/shared/makeupAnnualLimitSettings";
import {
  formatMakeupsAnnualLimitPhrase,
} from "@/features/makeups/shared/makeupAnnualLimitSettings";
import {
  DEFAULT_MAKEUPS_ANNUAL_PERIOD,
} from "@/features/makeups/shared/makeupAnnualPeriod";
import { DEFAULT_MAKEUPS_EXPIRY_IN_DAYS } from "@/features/makeups/shared/makeupExpirySettings";
import {
  formatMakeupsExpiryDurationPhrase,
  isMakeupsNoExpiry,
} from "@/features/makeups/shared/makeupExpirySettings";
import { useAccessToken, useTenantData } from "@/hooks";
import {
  fetchMakeupsEligibilitySettings,
  upsertMakeupsEligibilitySettings,
  type MakeupsEligibilitySettingsResult,
} from "@/lib/utils/makeup-settings/makeup-settings-api";
import { QueryKeys } from "@/lib/types";

type UseMakeupsEligibilitySettingsOptions = {
  enabled?: boolean;
};

export function useMakeupsEligibilitySettings({
  enabled = true,
}: UseMakeupsEligibilitySettingsOptions = {}) {
  const { tenantId } = useTenantData();
  const accessToken = useAccessToken();

  const query = useQuery({
    queryKey: [QueryKeys.MakeupEligibilitySettings, tenantId],
    queryFn: () => {
      if (!tenantId) {
        return {
          expiryDays: DEFAULT_MAKEUPS_EXPIRY_IN_DAYS,
          annualRequestLimit: DEFAULT_MAKEUPS_ANNUAL_REQUEST_LIMIT,
          annualPeriod: DEFAULT_MAKEUPS_ANNUAL_PERIOD,
          persisted: {
            expiry: false,
            annualLimit: false,
            annualPeriod: false,
          },
        } satisfies MakeupsEligibilitySettingsResult;
      }
      return fetchMakeupsEligibilitySettings(tenantId, {
        accessToken: accessToken ?? undefined,
      });
    },
    enabled: enabled && !!tenantId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  return {
    expiryDays: query.data?.expiryDays ?? DEFAULT_MAKEUPS_EXPIRY_IN_DAYS,
    annualRequestLimit:
      query.data?.annualRequestLimit ?? DEFAULT_MAKEUPS_ANNUAL_REQUEST_LIMIT,
    annualPeriod: query.data?.annualPeriod ?? DEFAULT_MAKEUPS_ANNUAL_PERIOD,
    persisted: query.data?.persisted ?? {
      expiry: false,
      annualLimit: false,
      annualPeriod: false,
    },
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useMakeupsEligibilitySettingsUpdate() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  return useMutation({
    mutationFn: async (request: {
      expiryDays: number;
      annualRequestLimit: number;
      annualPeriod: MakeupsEligibilitySettingsResult["annualPeriod"];
      persisted: MakeupsEligibilitySettingsResult["persisted"];
    }) => {
      if (!accessToken || !tenantId) {
        throw new Error("Authentication token is required");
      }
      return upsertMakeupsEligibilitySettings(tenantId, request, accessToken);
    },
    onSuccess: async (settings) => {
      if (tenantId) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.MakeupEligibilitySettings, tenantId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.MakeupExpiryInDays, tenantId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.MakeupAnnualRequestLimit, tenantId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.Attendance],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.MakeupSessions],
        });
      }

      const expiryPhrase = isMakeupsNoExpiry(settings.expiryDays)
        ? "no expiration limit"
        : formatMakeupsExpiryDurationPhrase(settings.expiryDays);
      const annualPhrase = formatMakeupsAnnualLimitPhrase(
        settings.annualRequestLimit,
        settings.annualPeriod,
      );

      toast({
        status: "success",
        title: "Makeup eligibility updated",
        description: `Request window: ${expiryPhrase}. Annual limit: ${annualPhrase}.`,
      });
    },
    onError: (error: unknown) => {
      toast({
        status: "error",
        title: "Could not save makeup eligibility",
        description:
          error instanceof Error ? error.message : "Something went wrong.",
      });
    },
  });
}
